import {Async} from 'Core/models/Async';
import {
  EmptyString,
  getNonEmptyStringErrors,
  isNonEmptyString,
  NonEmptyString,
  StringUnion,
} from 'Core/models/EmptyString';
import {Errors, ErrorsUnion, NoErrors} from 'Shared/components/ErrorPopover';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {SsoResponseDto} from 'Users/api';

const Username = 'Username';
const Password = 'Password';
const MinimumCharacterLimit = 5;
const MaximumCharacterLimitUsername = 45;
const MaximumCharacterLimitPassword = 50;
const atLeastCharacters = (target: string, size: number) =>
  Errors.standardErrors({value: [`${target} must be at least ${size} characters.`]});
const atMostCharacters = (target: string, size: number) =>
  Errors.standardErrors({value: [`${target} must be less than ${size} characters.`]});
const getUsernameErrors = (selectedUsername: StringUnion) =>
  isNonEmptyString(selectedUsername)
    ? selectedUsername.value.length < MinimumCharacterLimit
      ? atLeastCharacters(Username, MinimumCharacterLimit)
      : selectedUsername.value.length > MaximumCharacterLimitUsername
      ? atMostCharacters(Username, MaximumCharacterLimitUsername)
      : NoErrors
    : getNonEmptyStringErrors(selectedUsername);
const getPasswordErrors = (selectedPassword: StringUnion) =>
  isNonEmptyString(selectedPassword)
    ? selectedPassword.value.length < MinimumCharacterLimit
      ? atLeastCharacters(Password, MinimumCharacterLimit)
      : selectedPassword.value.length > MaximumCharacterLimitPassword
      ? atMostCharacters(Password, MaximumCharacterLimitPassword)
      : NoErrors
    : getNonEmptyStringErrors(selectedPassword);

export class LoadingExistingSSOAccount {
  public readonly key = 'LoadingSSOAccount';
  public username = ValidUsername.create(new EmptyString());
  public password = ValidPassword.create(new EmptyString());
  public isPrimary = false;
}

export class ValidUsername {
  public static create(username: StringUnion) {
    return isNonEmptyString(username) &&
      username.value.length >= MinimumCharacterLimit &&
      username.value.length <= MaximumCharacterLimitUsername
      ? new ValidUsername(username)
      : new InvalidUsername(username, getUsernameErrors(username));
  }

  public readonly key = 'ValidUsername';

  constructor(public readonly value: NonEmptyString) {}
}

export class ValidPassword {
  public static create(password: StringUnion) {
    return isNonEmptyString(password) &&
      password.value.length >= MinimumCharacterLimit &&
      password.value.length <= MaximumCharacterLimitPassword
      ? new ValidPassword(password)
      : new InvalidPassword(password, getPasswordErrors(password));
  }

  public readonly key = 'ValidPassword';

  constructor(public readonly value: NonEmptyString) {}
}

export class InvalidUsername {
  public readonly key = 'InvalidUsername';

  constructor(public readonly value: StringUnion, public readonly error: ErrorsUnion) {}
}

export class InvalidPassword {
  public readonly key = 'InvalidPassword';

  constructor(public readonly value: StringUnion, public readonly error: ErrorsUnion) {}
}

export class InvalidSSOAccount {
  public readonly key = 'InvalidSSOAccount';

  constructor(
    public readonly username: ValidUsername | InvalidUsername,
    public readonly password: ValidPassword | InvalidPassword,
    public readonly isPrimary: boolean
  ) {}
}

export class ValidSSOAccount {
  public static create(
    username: ValidUsername | InvalidUsername,
    password: ValidPassword | InvalidPassword,
    isPrimary: boolean
  ) {
    return username instanceof ValidUsername && password instanceof ValidPassword
      ? new ValidSSOAccount(username, password, isPrimary)
      : new InvalidSSOAccount(username, password, isPrimary);
  }

  public readonly key = 'ValidSSOAccount';
  private constructor(
    public readonly username: ValidUsername,
    public readonly password: ValidPassword,
    public readonly isPrimary: boolean
  ) {}
}

export class SavingSSOAccount {
  public static create(account: ValidSSOAccount) {
    return new SavingSSOAccount(account.username, account.password, account.isPrimary);
  }
  public readonly key = 'SavingSSOAccount';

  private constructor(
    public readonly username: ValidUsername,
    public readonly password: ValidPassword,
    public readonly isPrimary: boolean
  ) {}
}

export class SSOAccountWithError {
  public static create(account: ValidSSOAccount, errorMessage?: string) {
    return new SSOAccountWithError(account.username, account.password, account.isPrimary, errorMessage);
  }
  public readonly key = 'SSOAccountWithError';

  private constructor(
    public readonly username: ValidUsername,
    public readonly password: ValidPassword,
    public readonly isPrimary: boolean,
    public readonly errorMessage?: string
  ) {}
}

export type SSOAccount = (
  | LoadingExistingSSOAccount
  | SSOAccountWithError
  | ValidSSOAccount
  | InvalidSSOAccount
  | SavingSSOAccount
) & {
  ssoUrl?: Async<SsoResponseDto>;
};

export type SSOAccountOptions<TReturn> = ToMatchOptions<SSOAccount, 'key', TReturn>;

export const matchSSOAccount = <TReturn>(ssoAccount: SSOAccount, options: SSOAccountOptions<TReturn>): TReturn => {
  switch (ssoAccount.key) {
    case 'SSOAccountWithError':
      return options.SSOAccountWithError(ssoAccount);
    case 'LoadingSSOAccount':
      return options.LoadingSSOAccount(ssoAccount);
    case 'InvalidSSOAccount':
      return options.InvalidSSOAccount(ssoAccount);
    case 'ValidSSOAccount':
      return options.ValidSSOAccount(ssoAccount);
    case 'SavingSSOAccount':
      return options.SavingSSOAccount(ssoAccount);
  }
};

export const EmptySSOAccount = new InvalidSSOAccount(
  ValidUsername.create(new EmptyString()),
  ValidPassword.create(new EmptyString()),
  false
);
